import React, { useState, useEffect, useRef } from 'react';
import { Alert, Form } from 'react-bootstrap';
import CreatableSelect, { components } from 'react-select';
import Select from 'react-select';
import { getDefaultUserGoals, getDefaultUserTitles, getTitleAndGoal, updateProfileTags, hasNullValuesInTags } from 'lib/user';
import { useLoadScript } from "@react-google-maps/api";
import classes from "./OnboardingComponents.module.scss"


const libraries = ["places"];

const TheSentence = (props) => {

    const [profile, setProfile] = useState('');

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [title, setTitle] = useState('');
    const defaultTitles = getDefaultUserTitles();
    const [titleFilter, setTitleFilter] = useState('All');
    const [availableTitles, setAvailableTitles] = useState(defaultTitles);
    const [location, setLocation] = useState('');
    const [goal, setGoal] = useState('');
    const [displayedError, setDisplayedError] = useState('');
    const [tags, setTags] = useState([]);
    const [errors, setErrors] = useState({});

    const defaultGoals = getDefaultUserGoals();

    //Google Places API Key
    const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    const inputRef = useRef(null);


    //Loads the Google Places API
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: googleMapsKey,
        libraries: libraries,
    });

    /* Initialize and configure a Google Maps Autocomplete feature for a given input field. 
    Also updates sessionstorage when autofilling with dropdown menu*/
    useEffect(() => {
        if (isLoaded && inputRef.current) {
            const autocomplete = new window.google.maps.places.Autocomplete(
                inputRef.current,
                { types: ["(cities)"] }
            );

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
            
                let city = "Unknown";
                let state = "Unknown";
                let country = "Unknown";
            
                for (const component of place.address_components) {
                    if (component.types.includes("locality") || component.types.includes("administrative_area_level_3")) {
                        city = component.long_name;
                    } else if (component.types.includes("administrative_area_level_1")) {
                        state = component.short_name;
                    } else if (component.types.includes("country")) {
                        country = component.short_name;
                    }
                }
            
                const locationValue = `${city}, ${state}, ${country}`;
            
                setLocation(locationValue);
            
                setProfile(prevProfile => {
                    const updatedProfile = {
                        ...prevProfile,
                        location: locationValue
                    };
                    sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
                    return updatedProfile;
                });
            });
        }
    }, [isLoaded]);


    const filteredTitles = () => {
        if (titleFilter === 'All') {
            return defaultTitles;
        }
        return defaultTitles.filter(group => group.label === titleFilter);
    };

    // Loads the profile upon component mount
    useEffect(() => {
        // If the profile is already in session storage, load it
        const storedProfile = sessionStorage.getItem('profile');
        if (storedProfile) {
            const parsedProfile = JSON.parse(storedProfile);
            let tags = hasNullValuesInTags(parsedProfile.tags) ? [] : parsedProfile.tags;
            setFirstName(parsedProfile.fname || '');
            setLastName(parsedProfile.lname || '');
            setLocation(parsedProfile.location || '');
            setTags(tags);
            const { title: getTitle, goal: getGoal } = getTitleAndGoal(parsedProfile.tags);
            if (tags.length > 0) {
                const matchedTitle = defaultTitles.find(option => option.value === getTitle);
                setTitle(matchedTitle || { value: getTitle, label: getTitle });
            } else {
                setTitle('');
            }
            setGoal(defaultGoals.find(option => option.value === getGoal) || '');

            const backedUpProfile = {
                fname: parsedProfile.fname || '',
                lname: parsedProfile.lname || '',
                location: parsedProfile.location || '',
                tags: tags
            };

            setProfile(backedUpProfile);
        }
        else if (props.firstName || props.lastName || props.location || props.tags) {
                let tags = hasNullValuesInTags(props.tags) ? [] : props.tags;
                setFirstName(props.firstName || '');
                setLastName(props.lastName || '');
                setLocation(props.location || '');
                setTags(tags);
                const { title: getTitle, goal: getGoal } = getTitleAndGoal(props.tags);
                if (tags.length > 0) {
                    const matchedTitle = defaultTitles.find(option => option.value === getTitle);
                    setTitle(matchedTitle || { value: getTitle, label: getTitle });
                } else {
                    setTitle('');
                }
                setGoal(defaultGoals.find(option => option.value === getGoal) || '');
        
                const storedProfile = {
                    fname: props.firstName || '',
                    lname: props.lastName || '',
                    location: props.location || '',
                    tags: tags
                };
                setProfile(storedProfile);
                sessionStorage.setItem('profile', JSON.stringify(storedProfile));
        }
    }, [props.firstName, props.lastName, props.location, props.tags]);

    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.firstName;
            return newErrors;
        });

        const updatedProfile = {
            ...profile,
            fname: event.target.value
        };

        setProfile(updatedProfile);
        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    };

    const handleLastNameChange = (event) => {

        setLastName(event.target.value);

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.lastName;
            return newErrors;
        });

        const updatedProfile = {
            ...profile,
            lname: event.target.value
        };

        setProfile(updatedProfile);
        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    };

    const handleLocationChange = (event) => {
        setLocation(event.target.value);

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.location;
            return newErrors;
        });

        const updatedProfile = {
            ...profile,
            location: event.target.value
        };

        setProfile(updatedProfile);
        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    };

    const handleTitleChange = (titleInput) => {
        if (titleInput && !availableTitles.some(option => option.value === titleInput.value)) {
            // Add new title if it doesn't exist
            setTitle({ value: titleInput, label: titleInput, category: "Contribution" });
        }
        else {
            setTitle(titleInput);
        }

        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.title;
            return newErrors;
        });

        // Reformat the selected option to match the tags format
        titleInput = { display_name: titleInput.label, id: titleInput.role_name, category: titleInput.category };

        // Update the state with the combined tags, and remove the previous title tag
        const updatedTags = updateProfileTags(tags, titleInput, "contrib", true);
        setTags(updatedTags);

        const updatedProfile = {
            ...profile,
            tags: updatedTags
        };

        setProfile(updatedProfile);

        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    };

    const handleGoalChange = (goalInput) => {
        setGoal(goalInput);
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.goal;
            return newErrors;
        });

        // Reformat the selected option to match the tags format
        goalInput = { display_name: goalInput.label, id: goalInput.value, category: goalInput.category };

        // Update the state with the combined tags, and remove the previous title tag
        const updatedTags = updateProfileTags(tags, goalInput, "objective", true);
        setTags(updatedTags);
 
        const updatedProfile = {
            ...profile,
            tags: updatedTags
        };

        setProfile(updatedProfile);

        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    }

    /**
       * The function `handleTitleInputChange` limits the input value to 36 characters and then sets
       * available titles to default titles.
       * @param input - The `input` parameter in the `handleTitleInputChange` function represents the
       * value entered into a title input field. The function limits the input value to 36 characters and
       * sets available titles to a default list if the input exceeds this limit.
       * @returns If the input length is greater than 36 characters, nothing is being returned
       * explicitly. The function will exit early without setting any new available titles.
       */
    const handleTitleInputChange = (input) => {
        // Limit the input value to 36 characters
        if (input && input.length > 36) return;
        setAvailableTitles(defaultTitles);
    }

    // Handles the display for adding a role or not being able to add a role
    const NoOptionsMessage = (props) => {
        const {
            selectProps: { inputValue }
        } = props;

        const handleClick = () => {
            let formattedInput;
            // Format tag input values
            if (props.selectProps.onCreateOption.name === "handleTitleChange") {
                // Remove all special characters except dashes '-' and spaces ' '
                formattedInput = inputValue.replace(/\b\w/g, char => char.toUpperCase());
            }
            props.selectProps.onChange({ value: formattedInput, label: formattedInput });
            props.selectProps.onInputChange('');  // Clear the input value
            props.selectProps.onMenuClose();      // Close the dropdown
        }

        return (
            <components.NoOptionsMessage {...props}>
                <div className="select-no-options" onClick={handleClick}>
                    {inputValue ? `Add: ${inputValue}` : "No options"}
                </div>
            </components.NoOptionsMessage>
        );
    };

    /**
       * The function `validateFields` checks if certain fields are empty or not defined and returns an
       * object with any errors found.
       * @returns The function `validateFields` returns an object `newErrors` which contains error
       * messages for each field that is empty or not defined.
       * 
       * NOTE: This function is not being used currently, it should be used in a handleSubmit function that
       * will be made with backend implementation, so I wanted to keep it on hand, as the Sentence inputs must 
       * be validated
       */
    const validateFields = () => {
        const fields = [
            { name: "firstName", value: firstName },
            { name: "lastName", value: lastName },
            { name: "title", value: title.value },
            { name: "goal", value: goal.value },
            { name: "location", value: location },
        ];

        const newErrors = {};


        fields.forEach((field) => {
            if (!field.value || field.value.trim() === "") {

                newErrors[field.name] = `Field ${field.name} is empty or not defined`;
                setDisplayedError(`Field ${field.name} is empty or not defined`)
            }
        });

        return newErrors;
    };

    return (
        <div className={classes.theSentenceContainer}>
            <h1 className={classes.title}>Some quick questions</h1>
            <div className={classes.subtitle}>You <b>did</b> join for a reason, right?</div>
            <div className={classes.inputsContainer}>
                <div className={classes.sentenceText}>My name is </div>
                <div className={classes.inputBox}>
                    <Form.Control
                        type="text"
                        id="f_name"
                        value={firstName}
                        placeholder="First Name*"
                        onChange={handleFirstNameChange}
                    />
                </div>
                <div className={classes.inputBox}>
                    <Form.Control
                        type="text"
                        id="l_name"
                        value={lastName}
                        placeholder="Last Name*"
                        onChange={handleLastNameChange}
                    />
                </div>
                <div className={classes.sentenceText}> and I'm based in </div>
                <div className={classes.inputBox}>
                    <Form.Control
                        type="text"
                        id="location"
                        value={location}
                        placeholder="Enter a city*"
                        onChange={handleLocationChange}
                        ref={inputRef}
                    />
                </div>
                <div className={classes.sentenceText}>I am a </div>
                <div className={classes.selectionBox}>
                    <CreatableSelect
                        id="title"
                        value={title}
                        onInputChange={handleTitleInputChange}
                        onChange={handleTitleChange}
                        onCreateOption={handleTitleChange}
                        placeholder="Pick or add occupational title*"
                        options={filteredTitles()}
                        isSearchable
                        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                        components={{ NoOptionsMessage }}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        // menuPortalTarget={document.body}
                        menuPosition={'fixed'} // Required for portal to work correctly
                        menuPlacement={'auto'} // Top or bottom based on the available space
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Adjust z-index if necessary
                            control: (provided) => ({
                                ...provided,
                                borderColor: '#edecec',  // Set border color to gray
                                borderRadius: '0.75em',  // Set border radius to 0.75em
                                fontSize: '16px'
                            }),
                            option: (provided, state) => ({
                                ...provided
                            }),
                            groupHeading: (provided, state) => ({
                                ...provided,
                                padding: '10px',
                            })
                        }}
                    />
                </div>
                <div className={classes.sentenceText}> and I would like to </div>
                <div className={classes.selectionBox}>
                    <Select
                        id="goal"
                        value={goal}
                        onChange={handleGoalChange}
                        placeholder="goal for being here*"
                        options={defaultGoals}
                        menuPosition={'fixed'}
                        menuPlacement={'auto'}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Adjust z-index if necessary
                            control: (provided) => ({
                                ...provided,
                                borderColor: '#edecec',  // Set border color to gray
                                borderRadius: '0.75em',  // Set border radius to 0.75em
                                fontSize: '16px'
                            }),
                        }}
                    />
                </div>
            </div>
        </div>
    );


};

export default TheSentence;